import axios from "axios";
import { LoginResponse } from "../contexts/AuthContext";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3944";

// Créer une instance axios avec une configuration de base
const api = axios.create({
  baseURL: API_URL,
  timeout: 10000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Intercepteur pour ajouter automatiquement le token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Intercepteur pour gérer les erreurs
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expiré ou invalide
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

// Types pour les réponses
interface RegisterData {
  firstname: string;
  lastname: string;
  username: string;
  password: string;
  merchant_code: string;
}

interface SetPinData {
  pin: string;
}

interface VerifyPinData {
  pin: string;
}

// --- LOGIN ---
export async function login(username: string, password: string): Promise<LoginResponse> {
  try {
    const response = await api.post<LoginResponse>("/auth/login", {
      username,
      password,
    });
    
    const { access_token, token_type, role, user } = response.data;
    
    // Sauvegarde le token
    localStorage.setItem("token", access_token);
    localStorage.setItem("token_type", token_type);
    
    // Sauvegarde les infos utilisateur
    const userData = {
      ...user,
      role: role, // Utiliser le rôle retourné par le backend
      name: `${user.firstname || ''} ${user.lastname || ''}`.trim(),
    };
    localStorage.setItem("user", JSON.stringify(userData));

    console.log("✅ Login successful - User data:", {
      backendRole: role,
      backendNiveau: user.niveau,
      savedRole: userData.role,
      userData
    });
    
    return response.data;
  } catch (error: any) {
    console.error("Login error:", error);
    throw error.response?.data || { detail: "Erreur de connexion" };
  }
}

// --- REGISTER ---
export async function register(data: RegisterData) {
  try {
    const response = await api.post("/auth/register", data);
    return response.data;
  } catch (error: any) {
    console.error("Register error:", error);
    throw error.response?.data || { detail: "Erreur d'inscription" };
  }
}

// --- GET CURRENT USER ---
export async function getCurrentUser() {
  try {
    const token = localStorage.getItem("token");
    if (!token) {
      throw new Error("Aucun token trouvé");
    }

    const response = await api.get("/auth/me");
    return response.data;
  } catch (error: any) {
    console.error("Get current user error:", error);
    
    // Si l'erreur est 401, on nettoie le localStorage
    if (error.response?.status === 401) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
    }
    
    throw error.response?.data || { detail: "Erreur lors de la récupération de l'utilisateur" };
  }
}

// --- GET USER PROFILE ---
export async function getProfile() {
  try {
    const response = await api.get("/auth/me");
    return response.data;
  } catch (error: any) {
    console.error("Get profile error:", error);
    throw error.response?.data || { detail: "Erreur lors de la récupération du profil" };
  }
}

// --- SET SECURITY PIN ---
export async function setSecurityPin(pin: string) {
  try {
    const response = await api.post("/auth/set-pin", { pin });
    return response.data;
  } catch (error: any) {
    console.error("Set PIN error:", error);
    throw error.response?.data || { detail: "Erreur lors de la configuration du PIN" };
  }
}

// --- VERIFY SECURITY PIN ---
export async function verifySecurityPin(pin: string) {
  try {
    const response = await api.post("/auth/verify-pin", { pin });
    return response.data;
  } catch (error: any) {
    console.error("Verify PIN error:", error);
    throw error.response?.data || { detail: "Erreur lors de la vérification du PIN" };
  }
}

// --- CHECK IF HAS PIN ---
export async function hasPin() {
  try {
    const response = await api.get("/auth/has-pin");
    return response.data;
  } catch (error: any) {
    console.error("Check PIN error:", error);
    throw error.response?.data || { detail: "Erreur lors de la vérification du PIN" };
  }
}

// --- CHANGE PASSWORD ---
export async function changePassword(currentPassword: string, newPassword: string) {
  try {
    const response = await api.post("/auth/change-password", {
      currentPassword,
      newPassword,
    });
    return response.data;
  } catch (error: any) {
    console.error("Change password error:", error);
    throw error.response?.data || { detail: "Erreur lors du changement de mot de passe" };
  }
}

// --- SESSION TRACKING ---
export async function startSession(): Promise<string | null> {
  try {
    const response = await api.post("/sessions/start");
    const sessionToken = response.data.session_token;
    if (sessionToken) {
      localStorage.setItem("session_token", sessionToken);
    }
    return sessionToken;
  } catch (error) {
    console.error("Start session error:", error);
    return null;
  }
}

export async function endSession(reason: string = "manual"): Promise<void> {
  const sessionToken = localStorage.getItem("session_token");
  if (!sessionToken) return;
  try {
    await api.post("/sessions/end", { session_token: sessionToken, reason });
  } catch (error) {
    // Ignore errors - logout should not be blocked
  }
  localStorage.removeItem("session_token");
}

export async function sendHeartbeat(): Promise<void> {
  const sessionToken = localStorage.getItem("session_token");
  if (!sessionToken) return;
  try {
    await api.post("/sessions/heartbeat", { session_token: sessionToken });
  } catch (error) {
    // Ignore heartbeat errors silently
  }
}

// --- LOGOUT ---
export function logout(): void {
  try {
    // End session before cleanup (fire-and-forget)
    endSession("manual").catch(() => {});

    // Nettoyage local
    localStorage.removeItem("token");
    localStorage.removeItem("token_type");
    localStorage.removeItem("user");
    localStorage.removeItem("session_token");

    // Supprime le header Authorization des futures requêtes
    delete api.defaults.headers.common["Authorization"];
  } catch (error) {
    console.error("Logout error:", error);
  }
}

// --- VERIFY TOKEN ---
export async function verifyToken(): Promise<boolean> {
  try {
    const token = localStorage.getItem("token");
    if (!token) return false;

    // Vérifier si le token est expiré en le décodant
    const payload = JSON.parse(atob(token.split('.')[1]));
    const expirationTime = payload.exp * 1000; // Convertir en millisecondes
    const currentTime = Date.now();

    if (currentTime > expirationTime) {
      // Token expiré
      logout();
      return false;
    }

    // Optionnel: Vérifier avec le backend
    await api.get("/auth/me");
    return true;
  } catch (error) {
    console.error("Token verification error:", error);
    logout();
    return false;
  }
}

// --- REFRESH TOKEN ---
export async function refreshToken(): Promise<string | null> {
  try {
    const response = await api.post("/auth/refresh");
    const newToken = response.data.access_token;
    
    if (newToken) {
      localStorage.setItem("token", newToken);
      return newToken;
    }
    
    return null;
  } catch (error) {
    console.error("Token refresh error:", error);
    return null;
  }
}

// Fonction utilitaire pour obtenir le token
export function getToken(): string | null {
  return localStorage.getItem("token");
}

// Fonction utilitaire pour obtenir le type de token
export function getTokenType(): string | null {
  return localStorage.getItem("token_type");
}

// Fonction utilitaire pour obtenir les infos utilisateur stockées
export function getStoredUser(): any {
  const userStr = localStorage.getItem("user");
  return userStr ? JSON.parse(userStr) : null;
}

// Export de l'instance axios pour une utilisation directe si nécessaire
export { api };